import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Select, useToast } from '../components/common';
import { Spinner } from '@ofself/shell';
import { useGroup } from '../contexts/GroupContext';
import { REQUIREMENT_GROUPS } from '../utils/payoutRequirements';
import PayoutChecklist from '../components/dev/PayoutChecklist';
import api from '../services/api';
import './BillingPage.css';

/**
 * Money — the one page a developer must visit before charging for anything, and the
 * only place that says what they have been paid.
 *
 * "Money" rather than "Getting paid" because it is now both halves: how to be paid, and
 * what came in. (The buyer's page in nucleus stays "Billing" deliberately — there,
 * Allocations owns the word "money" as part of identity, and two subjects under one
 * word is the confusion that naming avoids. No such collision in the portal.)
 *
 * Payouts belong to the DEVELOPER, not to an app: somebody with four paid apps onboards
 * once. So this is a portal-level page rather than an app tab, and the app's Pricing tab
 * links here when it finds no usable account.
 *
 * NO BANK DETAIL IS COLLECTED OR DISPLAYED HERE. Stripe hosts the form and keeps
 * everything it gathers; we learn an account id and whether it works. The dashboard
 * button hands the developer to Stripe for payout history, because a second copy of that
 * is a breach target for data Stripe already shows them properly.
 *
 * THE TWO FACTS AT THE TOP are the merchant of record and the platform fee. Both are
 * things the platform holds itself to, and a developer needs them before pricing
 * anything — so they are stated plainly rather than buried in terms.
 */
/** Charges for itself — the same rule Home uses for its "A priced app" step. */
const isPriced = (app) => !!app?.pricing?.model && app.pricing.model !== 'free';

/** How recent a sync makes a second, fresh read pointless. */
const JUST_SYNCED_MS = 10000;
const justSynced = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) && Math.abs(Date.now() - t) < JUST_SYNCED_MS;
};

export default function BillingPage() {
  const toast = useToast();
  // WHOSE PAYOUTS follow the workspace switcher. A group's apps pay the GROUP's account
  // (DeveloperPayoutAccount.for_app), so onboarding from a group workspace must create
  // the group's account — this page used to always onboard the person, and a group app
  // then refused every checkout while this page said "Ready to sell".
  const { activeGroupId, activeGroup } = useGroup();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [earnings, setEarnings] = useState(null);
  // The owner's own apps, for "A priced app" — null while unknown.
  const [ownApps, setOwnApps] = useState(null);
  const [country, setCountry] = useState('US');
  const [changingCountry, setChangingCountry] = useState(false);
  // Set from the click until the browser leaves for Stripe — covers the page, because
  // creating the account and its link takes Stripe a few seconds and a greyed button
  // alone reads as a page that ignored the click.
  const [redirecting, setRedirecting] = useState(false);
  const [checking, setChecking] = useState(false);

  // ONLY THE LATEST LOAD MAY WRITE. Switch workspace while a load is in flight and the
  // old owner's answers can land after the new owner's — a group's sales would then
  // show under "Your personal payouts", the very mix-up this page exists to prevent.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;
    setLoading(true);
    // Clear the previous owner's figures now, not when the new ones arrive: until then
    // they would sit under the new workspace's heading — or stay there if it fails.
    setEarnings(null);
    setOwnApps(null);
    // Earnings are SEPARATELY tolerant of failure, and asked for at once rather than
    // after the account: the two are independent, and waiting serialised them. The
    // payout account is what this page is for; a developer who cannot see last month's
    // total can still finish onboarding, so a failure there must not take the page down.
    // Same owner as the account below: a group's sales are not personal earnings.
    api.getDeveloperEarnings(activeGroupId || undefined)
      .then(r => { if (current()) setEarnings(r); })
      .catch(() => { if (current()) setEarnings(null); });
    // WHETHER A PRICED APP EXISTS comes from the owner's app list, the same one Home
    // reads — not from earnings, which lists only apps that have already sold, so the
    // checklist said "Waiting" for a price until the first sale while Home said ready.
    const q = activeGroupId ? `?group_id=${encodeURIComponent(activeGroupId)}` : '';
    api.request(`/third-party/my-apps${q}`)
      .then(r => { if (current()) setOwnApps(r?.apps || []); })
      .catch(() => { if (current()) setOwnApps(null); });
    try {
      // Draw from the server's recent copy first — instant — then ask Stripe for the
      // live answer behind it, which is what someone back from onboarding needs.
      const account = await api.getPayoutAccount(activeGroupId || undefined);
      if (!current()) return;
      setData(account);
      setUnavailable(false);
      // ...unless the server synced with Stripe a moment ago to answer that read (its copy
      // was stale), in which case asking again would call Stripe twice in a row.
      if (account?.account && !justSynced(account.account.synced_at)) {
        api.getPayoutAccount(activeGroupId || undefined, { fresh: true })
          .then(r => { if (current()) setData(r); })
          .catch(() => { /* the copy on screen stands; the next visit retries */ });
      }
    } catch (e) {
      if (!current()) return;
      // 503 is not an error to apologise for: an instance that never enabled payments
      // is a legitimate deployment, and most self-hosted ones are exactly that.
      if (e?.status === 503 || /not configured|not enabled/i.test(e?.message || '')) {
        setUnavailable(true);
      } else {
        toast.error(e?.message || 'Could not load your payout account.');
      }
    } finally {
      if (current()) setLoading(false);
    }
  }, [toast, activeGroupId]);

  useEffect(() => { load(); }, [load]);
  // A different owner's account is a different account: drop any half-made choice.
  useEffect(() => { setChangingCountry(false); setCountry('US'); }, [activeGroupId]);

  // Returning from Stripe. The status is refreshed from Stripe on every read, so landing
  // back here shows the real state rather than whatever we had cached before they left.
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('onboarding');
    if (q === 'complete') toast.success('Welcome back — checking your account…');
    if (q === 'refresh') toast.info('That link expired. Start again below.');
  }, [location.search, toast]);

  // THE COUNTRY IS SENT ONLY WHEN IT IS BEING CHOSEN — a first setup, or an explicit
  // Change. Stripe cannot alter an account's country, so the backend answers a different
  // one by replacing the account; sending a default on every "Continue setup" would
  // replace accounts nobody asked to replace.
  const sendsCountry = !data?.account || changingCountry;
  const start = useCallback(async () => {
    setWorking(true);
    setRedirecting(true);
    try {
      const owner = activeGroupId ? { group_id: activeGroupId } : {};
      const res = await api.startPayoutOnboarding(
        sendsCountry ? { ...owner, country } : owner);
      // Never stored: Stripe expires these quickly, so a saved link is a dead link.
      window.location.href = res.onboarding_url;
    } catch (e) {
      toast.error(e?.message || 'Could not start onboarding.');
      setWorking(false);
      setRedirecting(false);
    }
  }, [toast, sendsCountry, country, activeGroupId]);

  // BACK BUT NOT GONE. Pressing Back on Stripe can restore this page from the browser's
  // back/forward cache exactly as it was left — cover up, buttons disabled — with no way
  // out but a reload. A restored page is a page nobody is waiting on: drop the cover.
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted) return;
      setRedirecting(false);
      setWorking(false);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // BACK FROM STRIPE, NOT YET READY. Stripe enables payouts a few seconds after the form
  // is submitted, so the read on landing often still says "unfinished" and only a manual
  // refresh showed "Ready to sell". Re-ask for about a minute instead — but ONLY when the
  // form was submitted: Stripe returns with ?onboarding=complete however the person
  // leaves, and someone who quit halfway is not about to become ready.
  const returning = new URLSearchParams(location.search).get('onboarding') === 'complete';
  const hasAccount = !!data?.account;
  const ready = !!data?.account?.can_charge;
  const submitted = !!data?.account?.details_submitted;
  useEffect(() => {
    if (!returning || !hasAccount || ready || !submitted) {
      setChecking(false);
      return undefined;
    }
    setChecking(true);
    // Chained timeouts, not an interval: the next ask waits for the last answer, so a
    // slow Stripe call cannot stack requests. `live` drops an answer that lands after a
    // workspace switch, so one owner's account never shows under another.
    let live = true;
    let tries = 0;
    let timer;
    const ask = async () => {
      tries += 1;
      try {
        const res = await api.getPayoutAccount(activeGroupId || undefined, { fresh: true });
        if (live) setData(res);
      } catch { /* the next try, or the manual refresh, will tell */ }
      if (!live) return;
      if (tries >= RECHECK_TRIES) setChecking(false);
      else timer = setTimeout(ask, RECHECK_MS);
    };
    timer = setTimeout(ask, RECHECK_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [returning, hasAccount, ready, submitted, activeGroupId]);

  const openDashboard = useCallback(async () => {
    setWorking(true);
    try {
      const res = await api.payoutDashboardLink(
        activeGroupId ? { group_id: activeGroupId } : {});
      window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e?.message || 'Could not open your Stripe dashboard.');
    } finally {
      setWorking(false);
    }
  }, [toast, activeGroupId]);

  if (loading) return <div className="billing-page">Loading…</div>;

  if (unavailable) {
    return (
      <div className="billing-page">
        <h1>Money</h1>
        <div className="billing-page__block is-muted">
          <b>Payments are not enabled on this ofSelf instance.</b>
          <span>
            Apps here cannot charge. A self-hosted instance can enable paid apps by
            configuring its own Stripe account — nothing about the identity graph depends
            on it.
          </span>
        </div>
      </div>
    );
  }

  const account = data?.account;
  // Until the app list answers (or if it fails), the apps that have sold are certainly priced.
  const pricedApps = ownApps ? ownApps.filter(isPriced) : (earnings?.apps || []);
  const fee = data?.platform_fee_bps ?? 0;
  const mor = data?.charge_mode === 'direct' ? 'you' : 'ofSelf';
  const countryPicker = (
    <CountrySelect codes={data?.supported_countries} value={country}
                   onChange={setCountry} disabled={working} />
  );

  return (
    <div className="billing-page">
      <h1>Money</h1>
      <p className="billing-page__owner">
        {activeGroupId
          ? <>Payouts for the group <b>{activeGroup?.name || 'this group'}</b>. Its apps are paid into this account.</>
          : <>Your personal payouts. Apps you own personally are paid into this account.</>}
      </p>

      {/* Where selling stands, at a glance — the same checklist Home shows. The
          steps' buttons are the account block's own, just below. */}
      <section className="billing-page__ck" aria-label="Getting paid">
        <span className="billing-page__ck-title">Getting paid</span>
        <PayoutChecklist account={account || null} pricedApps={pricedApps} brief />
      </section>

      {/* Published, not buried. These are the two facts a developer needs before they
          price anything, and both are commitments rather than settings. */}
      <div className="billing-page__terms">
        <div>
          <span className="billing-page__term-label">Merchant of record</span>
          <span className="billing-page__term-value">{mor}</span>
          <span className="billing-page__term-note">
            {mor === 'ofSelf'
              ? 'We are the seller of record: our name is on the buyer’s statement, and we handle sales tax, refunds and disputes. You need only a bank account.'
              : 'You are the seller of record: your name is on the buyer’s statement, and you handle sales tax, refunds and disputes.'}
          </span>
        </div>
        <div>
          <span className="billing-page__term-label">Platform fee</span>
          <span className="billing-page__term-value">
            {fee > 0 ? `${(fee / 100).toFixed(fee % 100 ? 2 : 0)}%` : 'none'}
          </span>
          <span className="billing-page__term-note">
            {fee > 0
              ? 'Taken from each sale before payout. Rounded down, never up.'
              : 'ofSelf takes nothing from your sales.'}
          </span>
        </div>
      </div>

      {!account ? (
        <div className="billing-page__block">
          <b>You have not set up payouts.</b>
          <span>
            Until you do, a paid app of yours cannot be bought — the checkout refuses
            rather than taking money we could not pay you. Stripe collects your details
            directly; we never see them.
          </span>
          {countryPicker}
          <Button onClick={start} disabled={working}>
            {working ? 'Opening Stripe…' : 'Set up payouts'}
          </Button>
        </div>
      ) : account.can_charge ? (
        <div className="billing-page__block">
          <b>Ready to sell.</b>
          <span>
            Your account can take payments and receive payouts.
            {account.country ? ` Country: ${countryName(account.country)}.` : ''}
            {account.default_currency
              ? ` Payouts in ${String(account.default_currency).toUpperCase()}.`
              : ''}
          </span>
          <Button onClick={openDashboard} disabled={working}>
            Open Stripe dashboard
          </Button>
        </div>
      ) : (
        <div className="billing-page__block is-warn">
          <b>
            {account.details_submitted
              ? 'Stripe needs more from you.'
              : 'Your setup is unfinished.'}
          </b>
          {checking && (
            <span className="billing-page__checking">
              <Spinner size={14} /> Checking with Stripe — this usually takes a few seconds.
            </span>
          )}
          <RequirementsList due={account.requirements_due} />
          {account.country && !changingCountry && (
            <span>
              Country: {countryName(account.country)}
              {/* Only while nothing is submitted: after that the backend refuses, because
                  replacing the account would discard what Stripe has already collected. */}
              {account.status === 'pending' && (
                <>
                  {' · '}
                  <button type="button" className="billing-page__link"
                          onClick={() => {
                            setCountry(String(account.country).toUpperCase());
                            setChangingCountry(true);
                          }}>
                    Change
                  </button>
                </>
              )}
            </span>
          )}
          {changingCountry && countryPicker}
          <span>
            {account.transfers_enabled && !account.payouts_enabled
              ? 'We can send you money but you cannot withdraw it yet, so selling stays disabled — holding a buyer’s money you could not access would be worse than refusing the sale.'
              : 'Paid apps of yours cannot be bought until this is finished.'}
          </span>
          <Button onClick={start} disabled={working}>
            {working ? 'Opening Stripe…' : 'Continue setup'}
          </Button>
        </div>
      )}

      <EarningsSection earnings={earnings} />

      {redirecting && (
        <div className="billing-page__redirect" role="status">
          <Spinner size={22} />
          <span>Taking you to Stripe…</span>
        </div>
      )}
    </div>
  );
}

const RECHECK_MS = 3000;
const RECHECK_TRIES = 20;

const REGION_NAMES = typeof Intl !== 'undefined' && Intl.DisplayNames
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

function countryName(code) {
  const upper = String(code).toUpperCase();
  try {
    return REGION_NAMES?.of(upper) || upper;
  } catch {
    return upper;
  }
}

/**
 * Where the payout account is. The list is the backend's — the countries Stripe can pay
 * out to from this platform — so a country Stripe would refuse is never offered.
 */
function CountrySelect({ codes, value, onChange, disabled }) {
  const options = (codes || ['US'])
    .map(code => ({ value: code, label: countryName(code) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return (
    // aria-label because FormGroup's <label> is not tied to its control.
    <Select label="Country you will be paid in" aria-label="Country you will be paid in"
            value={value} options={options}
            onChange={e => onChange(e.target.value)} disabled={disabled}
            helpText="Stripe cannot change this later." />
  );
}

/**
 * Stripe's field paths → what a person is actually being asked for.
 *
 * WHY THIS IS NOT THE RAW LIST. Showing `requirements_due` verbatim was well-intentioned
 * — naming what is missing beats sending somebody to a dashboard to guess — but a fresh
 * account returns fifteen entries like `identity.attestations.terms_of_service.account.ip`
 * and `identity.individual.dob.day`, one row per FIELD. That is Stripe's internal shape,
 * not a list of things to go and do, and a wall of it reads as a system in trouble rather
 * than a form somebody has not filled in yet.
 *
 * Worse, some of them cannot be actioned at all. The `attestations` entries are an IP
 * address and a timestamp that Stripe records WHEN YOU CLICK THROUGH ITS OWN FLOW —
 * there is nothing a developer could provide, and listing them as outstanding tasks
 * invites them to go looking for a field that does not exist.
 *
 * So: grouped into what each cluster means, with the raw list still one click away for
 * anyone who wants to reconcile against Stripe's dashboard.
 */

function RequirementsList({ due }) {
  const [showRaw, setShowRaw] = useState(false);
  const items = due || [];
  if (items.length === 0) return null;

  const seen = new Map();
  const unmatched = [];
  items.forEach(path => {
    const group = REQUIREMENT_GROUPS.find(g => g.test.test(path));
    if (group) {
      if (!seen.has(group.label)) seen.set(group.label, group);
    } else {
      // An unrecognised path is still shown rather than swallowed: Stripe adds fields,
      // and a requirement nobody can see is one nobody can satisfy.
      unmatched.push(path);
    }
  });

  return (
    <>
      <ul className="billing-page__reqs">
        {[...seen.values()].map(g => (
          <li key={g.label}>
            {g.label}
            {g.automatic && (
              <small> — Stripe records this when you accept, nothing to fill in</small>
            )}
          </li>
        ))}
        {unmatched.map(p => (
          <li key={p}>{p.replace(/_/g, ' ').replace(/\./g, ' → ')}</li>
        ))}
      </ul>
      <button type="button" className="billing-page__link"
              onClick={() => setShowRaw(v => !v)}>
        {showRaw ? 'Hide' : `Show all ${items.length} fields Stripe lists`}
      </button>
      {showRaw && (
        /* Verbatim, for reconciling against Stripe's own dashboard. */
        <ul className="billing-page__reqs is-raw">
          {items.map(p => <li key={p}><code>{p}</code></li>)}
        </ul>
      )}
    </>
  );
}

/**
 * What the apps have earned.
 *
 * TWO HALVES THAT DELIBERATELY DISAGREE, and saying so is the point. `Expected` is one
 * billing period at the prices people actually agreed to — forward-looking, and the
 * number to plan against. `Collected` is what Stripe has really charged — backward
 * looking, and the half that did not exist anywhere before. A developer shown one figure
 * labelled "revenue" would have no way to tell which question it answered.
 *
 * NOTHING HERE FORMATS MONEY. Every string comes from `display`, rendered by the same
 * `_money` the consent card and the store badge use. A `toFixed` in this file is how a
 * payouts page and a consent card come to quote different numbers for one app.
 *
 * CURRENCIES ARE NEVER SUMMED — the server returns them separately and this renders them
 * separately. £4 + $5 is a figure that is wrong in both.
 */
function EarningsSection({ earnings }) {
  if (!earnings) return null;

  const expected = Object.entries(earnings.expected_by_currency || {});
  const collected = Object.entries(earnings.collected_by_currency || {});
  const apps = earnings.apps || [];

  // Nothing sold yet. Said plainly rather than shown as a row of zeroes, which reads
  // like a broken page to someone who has simply not made a sale.
  if (!apps.length) {
    return (
      <section className="billing-page__earnings">
        <h2>Earnings</h2>
        <p className="billing-page__empty">
          None of your apps have earned anything yet. Free apps never appear here.
        </p>
      </section>
    );
  }

  return (
    <section className="billing-page__earnings">
      <h2>Earnings</h2>

      <div className="billing-page__totals">
        <div>
          <span className="billing-page__term-label">Expected each period</span>
          {expected.map(([cur, b]) => (
            <span key={cur} className="billing-page__term-value">
              {b.display ? b.display.net : `${b.net} ${cur}`}
              {b.platform_fee > 0 && b.display && (
                <small> after {b.display.platform_fee} fee</small>
              )}
            </span>
          ))}
          <span className="billing-page__term-note">
            One billing period at the prices people agreed to. A grandfathered
            subscriber counts at their price, not today’s.
          </span>
        </div>
        <div>
          <span className="billing-page__term-label">Collected so far</span>
          {collected.length === 0 ? (
            <span className="billing-page__term-value">nothing yet</span>
          ) : collected.map(([cur, b]) => (
            <span key={cur} className="billing-page__term-value">
              {b.display ? b.display.net : `${b.net} ${cur}`}
            </span>
          ))}
          <span className="billing-page__term-note">
            What Stripe has actually charged. Your Stripe dashboard holds the record of
            what has reached your bank.
          </span>
        </div>
      </div>

      <div className="billing-page__table-wrap">
        <table className="billing-page__table">
          <thead>
            <tr>
              <th>App</th><th>Price</th><th>Subscribers</th>
              <th>Expected</th><th>Collected</th>
            </tr>
          </thead>
          <tbody>
            {apps.map(row => (
              <tr key={row.app_id}>
                <td>{row.app_name}</td>
                <td>{row.pricing_display?.headline || '—'}</td>
                <td>
                  {row.counts.active}
                  {row.counts.past_due > 0 && (
                    /* Paused, not lost — a failed card, and the money is still owed.
                       Folding it into `active` would hide a fixable problem. */
                    <small className="is-attention"> +{row.counts.past_due} past due</small>
                  )}
                </td>
                <td>{Object.entries(row.expected_by_currency).map(([c, b]) => (
                  <div key={c}>{b.display ? b.display.net : `${b.net} ${c}`}</div>
                )) || '—'}</td>
                <td>{Object.entries(row.collected_by_currency).length === 0 ? '—'
                  : Object.entries(row.collected_by_currency).map(([c, b]) => (
                    <div key={c}>{b.display ? b.display.net : `${b.net} ${c}`}</div>
                  ))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {earnings.note && <p className="billing-page__note">{earnings.note}</p>}
    </section>
  );
}
